/* eslint-disable no-nested-ternary */
import { faArrowUpFromBracket } from '@fortawesome/free-solid-svg-icons/faArrowUpFromBracket';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons/faCircleUser';
import { faCrown } from '@fortawesome/free-solid-svg-icons/faCrown';
import { faGear } from '@fortawesome/free-solid-svg-icons/faGear';
import { faLink } from '@fortawesome/free-solid-svg-icons/faLink';
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle';
import { faSun } from '@fortawesome/free-solid-svg-icons/faSun';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons/faUserCircle';
import { faUsersGear } from '@fortawesome/free-solid-svg-icons/faUsersGear';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { Themes, useTheme } from '../ThemeContext';
import { authTestIDs } from '../constants/test/auth.testIds';
import { UserRoles } from '../model/user/types/UserRoles.enum';
import { adminSliceSelectors } from '../redux/admin/admin.selector';
import { adminActions } from '../redux/admin/admin.slice';
import { atiraLinkSliceSelectors } from '../redux/atira-link/atira-link.selector';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { userSliceSelectors } from '../redux/user/user.selector';
import { userActions } from '../redux/user/user.slice';
import { NotificationsDrawer } from '../routes/shared/components/NotificationsDrawer';
import { SelectLinkToManageModal } from '../routes/shared/components/SelectLinkToManageModal';
import { ShareDrawer } from '../routes/shared/components/ShareDrawer';
import { Breakpoints } from '../theme/Breakpoints';
import { FontSize } from '../theme/FontSize';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { truncateString } from '../utils/String';
import { Badge, MenuProps, Tooltip } from '../utils/local-antd';
import { AtiraDropdown } from './AtiraDropdown';
import { AtiraIcon } from './AtiraIcon';
import { Button } from './Button';
import { CopyButton } from './CopyButton';
import { Flex } from './Flex';
import { AtiraImage } from './Image';
import { ImageCreateEdit } from './ImageCreateEdit';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Text } from './Text';

const Container = styled(Flex)`
  align-items: center;
  gap: ${Spacing.m};

  @media (min-width: ${Breakpoints.LARGE_DESKTOP}) {
    gap: ${Spacing.l};
  }
`;

const UserProfileWrapper = styled(Flex)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Rounded.circle};
  width: 4rem;
  height: 4rem;
  overflow: hidden;
  cursor: pointer;
`;

const StyledButton = styled(Button)`
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.textColor};
  margin: 0;
  padding: 0;
`;

const StyledCopyButton = styled(CopyButton)`
  padding: 0;
`;

const StyledSubscribeLink = styled(Link)`
  color: ${({ theme }) => theme.main};
  transition: all 0.3s;

  &:hover {
    color: ${({ theme }) => theme.mainHover};
  }
`;

const StyledBadge = styled(Badge)`
  display: flex;
`;

const ManageLinksButton = styled(StyledButton)`
  position: relative;
`;

const SelectedLinkImageWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.onMain};
  position: absolute;
  top: -4px;
  right: 5px;
  width: 1rem;
  aspect-ratio: 1 / 1;
  border-radius: ${Rounded.circle};
  overflow: hidden;

  html[dir='rtl'] & {
    right: -5px;
  }

  .ant-image {
    width: 100% !important;
    height: 100% !important;
  }

  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    display: block;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ThemedDropdown = createGlobalStyle`
  .ant-dropdown-menu-item-group-list {
    margin: 0 !important;
  }

  .ant-dropdown-menu-item:first-child {
    cursor: default !important;
  }

  .ant-dropdown-menu-item.divider:hover {
    background-color: transparent !important;
    cursor: default;
  }
`;

const DropdownItem = styled(Flex)`
  align-items: center;
  font-size: ${FontSize.m};
  color: ${({ theme }) => theme.fgPrimary};

  svg {
    width: 1.5rem;
    margin-inline-end: ${Spacing.s};
  }
`;

export const UserHeader: React.FC = () => {
  const [notificationsDrawerVisible, setNotificationsDrawerVisible] =
    useState(false);
  const [selectLinkModalVisible, setSelectLinkModalVisible] = useState(false);
  const [shareDrawerVisible, setShareDrawerVisible] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { theme, toggleTheme, currentTheme } = useTheme();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const unreadMessages = useAppSelector(
    adminSliceSelectors.selectAdminMessages,
  )?.filter(({ read }) => read === false);
  const selectedLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const userLinks = useAppSelector(
    atiraLinkSliceSelectors.selectUserAtiraLinks,
  );

  const adminMessagesPage = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPage,
  );
  const adminMessagesPageSize = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPageSize,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const onLogout = async () => {
    await dispatch(userActions.logout());
    navigate('/login', { replace: true });
  };

  const items: MenuProps['items'] = [
    {
      key: 'g1',
      type: 'group',
      children: [
        {
          key: 'email',
          label: (
            <Flex alignItems="center" justifyContent="space-between" gap="s">
              <Text fontSize="m" color="fgPrimary">
                <Tooltip title={user!.email}>
                  {truncateString(user!.email)}
                </Tooltip>
              </Text>
              <StyledCopyButton
                copy={user!.email}
                iconSize="1x"
                successText="common.copy-success"
                color="fgSecondary"
              />
            </Flex>
          ),
        },
      ],
    },
    {
      key: 'divider-1',
      label: <hr />,
      className: 'divider',
    },
    ...(userLinks.length
      ? [
          {
            key: 'share',
            label: (
              <DropdownItem>
                <AtiraIcon icon={faArrowUpFromBracket} />
                {t('common.share')}
              </DropdownItem>
            ),
            onClick: () => setShareDrawerVisible(true),
          },
        ]
      : []),
    ...(user?.roles.includes(UserRoles.ADMIN)
      ? [
          {
            key: 'admin-panel',
            label: (
              <DropdownItem>
                <AtiraIcon icon={faUsersGear} />
                {t('admin.header.title')}
              </DropdownItem>
            ),
            onClick: () => navigate('/admin'),
          },
        ]
      : []),
    {
      key: 'settings',
      label: (
        <DropdownItem>
          <AtiraIcon icon={faGear} />
          {t('common.settings')}
        </DropdownItem>
      ),
      onClick: () => navigate('/settings'),
    },
    {
      key: 'theme',
      label: (
        <DropdownItem>
          <AtiraIcon icon={currentTheme === Themes.DARK ? faSun : faMoon} />
          {t(`darkMode.${currentTheme === Themes.DARK ? 'off' : 'on'}`)}
        </DropdownItem>
      ),
      onClick: toggleTheme,
    },

    {
      key: 'divider-2',
      label: <hr />,
      className: 'divider',
    },
    {
      key: 'logout',
      label: (
        <Button
          onClick={onLogout}
          fontSize="1rem"
          backgroundColor="danger"
          color="onDanger"
          textAlign="center"
          width="100%"
          dataTestId={authTestIDs.logout.submit}
        >
          {t('header.logout')}
        </Button>
      ),
      className: 'logout-button',
    },
  ];

  useEffect(() => {
    dispatch(
      adminActions.getAdminMessages({
        userId,
        meta: { page: adminMessagesPage - 1, count: adminMessagesPageSize },
      }),
    );
  }, [dispatch, userId, adminMessagesPage, adminMessagesPageSize]);

  return (
    <Container>
      <Tooltip title={t('header.subscription')}>
        <StyledSubscribeLink to={'/subscription'}>
          <AtiraIcon icon={faCrown} size="2xl" />
        </StyledSubscribeLink>
      </Tooltip>

      <Tooltip title={t('links.tooltip')}>
        <ManageLinksButton
          onClick={() => setSelectLinkModalVisible(true)}
          icon={faLink}
          iconWidth="2xl"
          iconColor="iconPrimary"
        >
          <SelectedLinkImageWrapper>
            {selectedLink?.image ? (
              <ImageCreateEdit src={selectedLink.image} />
            ) : (
              <AtiraIcon
                icon={selectedLink ? faUserCircle : faPlusCircle}
                color="main"
              />
            )}
          </SelectedLinkImageWrapper>
        </ManageLinksButton>
      </Tooltip>

      <LanguageSwitcher color="iconPrimary" />

      <Tooltip title={t('common.notifications')}>
        <StyledBadge
          count={unreadMessages?.length}
          color={unreadMessages?.length ? theme.main : theme.transparent}
        >
          <StyledButton
            icon={faBell}
            iconWidth="2x"
            onClick={() => setNotificationsDrawerVisible(true)}
            iconColor="iconPrimary"
          />
        </StyledBadge>
      </Tooltip>

      <AtiraDropdown
        overlayStyle={{
          width: '16rem',
          borderRadius: '0.5rem',
        }}
        items={items}
        placement="bottomRight"
        arrow={{ pointAtCenter: true }}
        trigger={['click']}
      >
        <UserProfileWrapper>
          {user?.picture ? (
            <AtiraImage src={user.picture} />
          ) : (
            <AtiraIcon
              icon={faCircleUser}
              size="4x"
              color={currentTheme === Themes.DARK ? 'onMain' : 'main'}
            />
          )}
        </UserProfileWrapper>
      </AtiraDropdown>

      <ThemedDropdown />

      <NotificationsDrawer
        open={notificationsDrawerVisible}
        onClose={() => setNotificationsDrawerVisible(false)}
      />

      <SelectLinkToManageModal
        open={selectLinkModalVisible}
        onClose={() => setSelectLinkModalVisible(false)}
      />

      <ShareDrawer
        open={shareDrawerVisible}
        onClose={() => setShareDrawerVisible(false)}
      />
    </Container>
  );
};
